/**
 * Summary tab — legacy process × date pivot (`renderSummary` in productionOutput_v2.js) with a
 * metric switcher: rows are processes, columns are Total plus each date in the window.
 *
 * The server returns long form `(operation, daily_date, pieces, …)`; arranging those cells into a
 * matrix is presentation — every number is already computed in SQL (§4.3).
 */

import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/DataTable';
import { ButtonGroupSelector } from '@/components/report/ButtonGroupSelector';
import { decimal, integer } from '@/components/report/groupedColumns';

export interface SummaryRow {
  factory: string;
  sort_order: number;
  operation: string;
  daily_date: string;
  pieces: number;
  produced_mins: number | null;
  revenue_hkd: number | null;
  material_cost_hkd: number | null;
  material_cost_pct: number | null;
  range_material_cost_pct: number | null;
}

export type SummaryMetric = 'pcs' | 'smv' | 'revenue' | 'matcostval' | 'matcost';

const METRICS: { id: SummaryMetric; label: string }[] = [
  { id: 'pcs', label: 'Quantity' },
  { id: 'smv', label: 'Produced mins' },
  { id: 'revenue', label: 'Revenue (HKD)' },
  { id: 'matcostval', label: 'Material cost (HKD)' },
  { id: 'matcost', label: 'Material cost %' },
];

/** Operations that carry minutes / revenue / cost in legacy SUMMARY_ROWS. */
const DETAILED = new Set(['Sewing', 'QC', 'Shipment']);

function isoDate(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function cellValue(row: SummaryRow, metric: SummaryMetric): number | null {
  switch (metric) {
    case 'pcs':
      return row.pieces ?? 0;
    case 'smv':
      return row.produced_mins;
    case 'revenue':
      return row.revenue_hkd;
    case 'matcostval':
      return row.material_cost_hkd;
    case 'matcost':
      return row.material_cost_pct;
  }
}

function formatCell(value: number | null, metric: SummaryMetric): React.ReactNode {
  if (value === null) {
    return <span className="text-muted-foreground/40">—</span>;
  }
  if (metric === 'matcost') {
    return `${decimal.format(value)}%`;
  }
  return integer.format(Math.round(value));
}

/** One display row of the process × date matrix. */
export interface MatrixRow {
  sort_order: number;
  operation: string;
  total: number | null;
  /** date ISO → metric value */
  byDate: Record<string, number | null>;
}

/** Places server-computed cells into the matrix; percentages come from SQL, never averaged here. */
function buildMatrix(rows: SummaryRow[], metric: SummaryMetric): {
  dates: string[];
  matrix: MatrixRow[];
} {
  const applicable = rows.filter((r) => {
    if (metric === 'pcs') return true;
    return DETAILED.has(r.operation);
  });

  const dateSet = new Set<string>();
  for (const r of applicable) {
    const d = isoDate(r.daily_date);
    if (d !== '') dateSet.add(d);
  }
  const dates = [...dateSet].sort();

  const byOp = new Map<string, SummaryRow[]>();
  for (const r of applicable) {
    const list = byOp.get(r.operation) ?? [];
    list.push(r);
    byOp.set(r.operation, list);
  }

  const operations = [...byOp.entries()]
    .map(([operation, list]) => ({
      operation,
      sort_order: list[0]?.sort_order ?? 99,
      list,
    }))
    .sort((a, b) => a.sort_order - b.sort_order);

  const matrix: MatrixRow[] = operations.map(({ operation, sort_order, list }) => {
    const byDate: Record<string, number | null> = {};
    let totalSum = 0;
    let seen = false;

    if (metric === 'matcost') {
      for (const r of list) {
        const d = isoDate(r.daily_date);
        byDate[d] = r.material_cost_pct;
      }
    } else {
      // Long form is factory × date, so an all-factory selection needs the dates summing across it.
      for (const r of list) {
        const d = isoDate(r.daily_date);
        const v = cellValue(r, metric);
        if (v === null) continue;
        byDate[d] = (byDate[d] ?? 0) + v;
        totalSum += v;
        seen = true;
      }
    }

    const total = metric === 'matcost'
      ? (list.find((row) => row.range_material_cost_pct !== null)?.range_material_cost_pct ?? null)
      : seen
        ? totalSum
        : null;

    return { sort_order, operation, total, byDate };
  });

  return { dates, matrix };
}

export function SummaryTable({
  rows,
  isLoading,
  metric: metricProp,
  onMetricChange,
  truncated = false,
}: {
  rows: SummaryRow[];
  isLoading: boolean;
  metric?: SummaryMetric;
  onMetricChange?: (metric: SummaryMetric) => void;
  /** Never build a matrix from a server-capped subset (§4.3); ReportPage explains the truncation. */
  truncated?: boolean;
}) {
  const [internalMetric, setInternalMetric] = useState<SummaryMetric>('pcs');
  const metric = metricProp ?? internalMetric;
  const setMetric = onMetricChange ?? setInternalMetric;

  const { dates, matrix } = useMemo(() => buildMatrix(rows, metric), [rows, metric]);

  const columns = useMemo<ColumnDef<MatrixRow>[]>(() => {
    const cols: ColumnDef<MatrixRow>[] = [
      {
        id: 'operation',
        accessorKey: 'operation',
        header: 'Process',
        enableSorting: false,
      },
      {
        id: 'total',
        header: 'Total',
        enableSorting: false,
        meta: { align: 'right', headerClassName: 'font-semibold' },
        cell: ({ row }) => (
          <span className="font-medium">{formatCell(row.original.total, metric)}</span>
        ),
      },
    ];
    for (const d of dates) {
      cols.push({
        id: `d:${d}`,
        header: d,
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => formatCell(row.original.byDate[d] ?? null, metric),
      });
    }
    return cols;
  }, [dates, metric]);

  if (truncated) return null;

  return (
    <div className="space-y-3">
      <ButtonGroupSelector
        label="Metric"
        options={METRICS.map((m) => ({ value: m.id, label: m.label }))}
        value={metric}
        onChange={setMetric}
      />

      <DataTable
        columns={columns}
        data={matrix}
        isLoading={isLoading}
        getRowId={(row) => row.operation}
        caption="Production summary by process and date"
        emptyMessage="Nothing was recorded for these filters. Widen the dates, or clear the factory and brand."
      />
    </div>
  );
}
